import { RadioGroup } from '@headlessui/react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { WeekdayName, WeekdayNameSchema } from '@/modules/location/domain/Weekday.ts';
import { EmitterSubscriber } from '@/shared/hooks/useEmitter';
import { useCallback, useEffect, useRef } from 'react';

type Direction = 'previous' | 'next';

export type RowChangeEvent = {
    direction: Direction;
    currentRow: number;
    currentWeekday: WeekdayName;
};

export type WeekDayFocusEvent = {
    weekday: WeekdayName;
    row: number;
};

type WeekDaySelectionProps = {
    rowIndex: number;
    selected: WeekdayName[];
    onSelectionChange: (weekday: WeekdayName) => void;
    onRowChange: (event: RowChangeEvent) => void;
    focusEmitter?: EmitterSubscriber<WeekDayFocusEvent>;
};

export const WeekdaySelection = (props: WeekDaySelectionProps) => {
    const { t } = useTranslation();
    const getWeekdayTranslation = (weekday: WeekdayName): string =>
        ({
            monday: t('location.openingTimes.weekdayMonday'),
            tuesday: t('location.openingTimes.weekdayTuesday'),
            wednesday: t('location.openingTimes.weekdayWednesday'),
            thursday: t('location.openingTimes.weekdayThursday'),
            friday: t('location.openingTimes.weekdayFriday'),
            saturday: t('location.openingTimes.weekdaySaturday'),
            sunday: t('location.openingTimes.weekdaySunday'),
        })[weekday] || weekday;

    function isSelected(weekday: WeekdayName) {
        return props.selected.includes(weekday);
    }

    function handleSelection(e: React.KeyboardEvent<HTMLDivElement>, weekday: WeekdayName) {
        e.preventDefault();
        props.onSelectionChange(weekday);
    }

    function handleVerticalArrow(e: React.KeyboardEvent<HTMLDivElement>, weekday: WeekdayName, index: number) {
        e.preventDefault();
        e.stopPropagation();
        props.onRowChange({
            direction: e.key === 'ArrowUp' ? 'previous' : 'next',
            currentRow: index,
            currentWeekday: weekday,
        });
    }

    const weekdayRefs = useRef<Map<WeekdayName, HTMLElement | null>>(new Map());

    const handleFocusEventFromParent = useCallback(
        (event: WeekDayFocusEvent) => {
            if (event.row !== props.rowIndex) return;
            weekdayRefs.current.get(event.weekday)?.focus();
        },
        [props.rowIndex]
    );

    useEffect(() => {
        if (!props.focusEmitter) return;
        return props.focusEmitter.subscribe(handleFocusEventFromParent);
    }, [handleFocusEventFromParent, props.focusEmitter]);

    return (
        <RadioGroup
            value={props.rowIndex}
            onChange={() => {}}
            className="flex w-full pl-px"
            data-test="weekday-selection"
        >
            {WeekdayNameSchema.options.flatMap(weekday => (
                <RadioGroup.Option
                    key={weekday}
                    data-test={`weekday-option-${weekday}`}
                    value={props.rowIndex}
                    data-checked={isSelected(weekday)}
                    data-row-index={props.rowIndex}
                    data-row-weekday={weekday}
                    ref={(el: HTMLElement | null) => {
                        weekdayRefs.current.set(weekday, el);
                    }}
                    className={({ active }) =>
                        clsx(
                            'font-copy-md hover:border-interactive-hovered -ml-px flex h-[48px] min-w-[43px] flex-1 cursor-pointer items-center justify-center border first:rounded-l first:border-l last:rounded-r last:border-r hover:z-10 focus:outline-none',
                            {
                                'hover:bg-surface-selected': !isSelected(weekday),
                                'bg-surface-selected': active && !isSelected(weekday),
                                'bg-surface-active text-dark hover:bg-surface-dark-selected': isSelected(weekday),
                                'bg-surface': !active && !isSelected(weekday),
                                'bg-surface-dark-selected': active && isSelected(weekday),
                            }
                        )
                    }
                    onClick={() => {
                        props.onSelectionChange(weekday);
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            handleSelection(e, weekday);
                        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            handleVerticalArrow(e, weekday, props.rowIndex);
                        }
                    }}
                    onKeyUp={e => {
                        if (e.key === ' ') {
                            handleSelection(e, weekday);
                        }
                    }}
                >
                    {getWeekdayTranslation(weekday)}
                </RadioGroup.Option>
            ))}
        </RadioGroup>
    );
};
